import React, { useState, useEffect, useRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  IconButton
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';

const styles = (theme) => ({
  section: {
    padding: '60px 0',
    width: '100%',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '60px',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  sectionNumber: {
    fontSize: '14px',
    letterSpacing: '3px',
    color: '#d4145a',
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: 600,
    marginBottom: '15px',
  },
  sectionTitleText: {
    fontSize: '48px',
    marginTop: '15px',
    marginBottom: '20px',
    fontWeight: 700,
    color: '#1a1a1a',
    fontFamily: '"Georgia", serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '32px',
    },
  },
  sectionDescription: {
    fontSize: '18px',
    color: '#555',
    maxWidth: '700px',
    margin: '0 auto',
    fontFamily: '"Segoe UI", sans-serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  testimonialsContainer: {
    position: 'relative',
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px 40px',
  },
  testimonialCard: {
    padding: '40px',
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '5px',
    margin: '20px 0',
    position: 'relative',
    transition: 'all 0.3s',
    '&:hover': {
      borderColor: '#d4145a',
      boxShadow: '0 10px 40px rgba(212, 20, 90, 0.1)',
    },
  },
  quoteIcon: {
    color: '#d4145a',
    opacity: 0.4,
    fontSize: '40px',
    marginBottom: '20px',
  },
  testimonialText: {
    fontSize: '18px',
    lineHeight: 1.8,
    color: '#333',
    fontStyle: 'italic',
    marginBottom: '30px',
    fontFamily: '"Georgia", serif',
  },
  testimonialAuthor: {
    display: 'flex',
    alignItems: 'center',
  },
  authorInfo: {
    marginLeft: '15px',
  },
  authorName: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '5px',
    fontFamily: '"Georgia", serif',
  },
  authorRole: {
    fontSize: '14px',
    color: '#666',
    fontFamily: '"Segoe UI", sans-serif',
  },
  navigationArrows: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '30px',
  },
  navArrow: {
    margin: '0 10px',
    color: '#1e3a5f',
    border: '2px solid #1e3a5f',
    borderRadius: '50%',
    padding: '8px',
    transition: 'all 0.3s',
    '&:hover': {
      background: '#1e3a5f',
      color: 'white',
    },
    '&.Mui-disabled': {
      opacity: 0.3,
      cursor: 'not-allowed',
    },
  },
  indicatorsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  },
  indicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#e0e0e0',
    margin: '0 5px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  activeIndicator: {
    background: '#d4145a',
    transform: 'scale(1.5)',
  },
  avatarCircle: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#1e3a5f',
    fontWeight: 'bold',
    fontSize: '20px',
    fontFamily: '"Georgia", serif',
  },
});

function TestimonialsSection(props) {
  const { classes } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  // Auto-scroll functionality
  useEffect(() => {
    // Start auto-scroll when component mounts
    startAutoScroll();
    
    // Clean up interval when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  // Reset the interval whenever activeIndex changes
  useEffect(() => {
    if (!isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      startAutoScroll();
    }
  }, [activeIndex, isPaused]);
  
  const startAutoScroll = () => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => {
        const newIndex = prevIndex < testimonials.length - 1 ? prevIndex + 1 : 0;
        return newIndex;
      });
    }, 5000); // Change testimonial every 5 seconds
  };

  // Testimonial data
  const testimonials = [
    {
      text: "LegalSutra handled my property dispute with remarkable professionalism and expertise. Their attention to detail and deep knowledge of property laws in India led to a favorable judgment in the High Court. I highly recommend their services.",
      name: "Rajesh Kumar",
      location: "Delhi",
      initials: "RK",
      case: "Property Dispute"
    },
    {
      text: "When facing complex criminal charges, LegalSutra provided exceptional legal representation. Their meticulous preparation and strategic approach made all the difference in my case. They were not only professional but also compassionate throughout the entire process.",
      name: "Anita Sharma",
      location: "Noida",
      initials: "AS",
      case: "Criminal Defense"
    },
    {
      text: "During my divorce proceedings, LegalSutra offered clear guidance and strong advocacy. They helped negotiate fair terms for asset division and child custody while minimizing conflict. Their expertise in family law made a difficult time much easier to navigate.",
      name: "Vijay Mehta",
      location: "Mumbai",
      initials: "VM",
      case: "Divorce Settlement"
    }
  ];

  // Handle navigation
  const handleNext = () => {
    setActiveIndex((prevIndex) => 
      prevIndex < testimonials.length - 1 ? prevIndex + 1 : 0
    );
    handleUserInteraction();
  };

  const handlePrev = () => {
    setActiveIndex((prevIndex) => 
      prevIndex > 0 ? prevIndex - 1 : testimonials.length - 1
    );
    handleUserInteraction();
  };

  const goToTestimonial = (index) => {
    setActiveIndex(index);
    handleUserInteraction();
  };
  
  // Pause auto-scroll temporarily when user interacts with controls
  const handleUserInteraction = () => {
    setIsPaused(true);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Resume after a delay
    setTimeout(() => {
      setIsPaused(false);
    }, 8000); // Resume auto-scroll after 8 seconds of inactivity
  };

  return (
    <section className={classes.section} id="testimonials">
      <div className={classes.sectionHeader}>
        <div className={classes.sectionNumber}>III. TESTIMONIALS</div>
        <Typography variant="h2" className={classes.sectionTitleText}>
          Client Success Stories
        </Typography>
        <Typography className={classes.sectionDescription}>
          Read what our clients have to say about their experiences working with us
        </Typography>
      </div>

      <div className={classes.testimonialsContainer}>
        <div 
          className={classes.testimonialCard}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <FormatQuoteIcon className={classes.quoteIcon} />
          <Typography className={classes.testimonialText}>
            {testimonials[activeIndex].text}
          </Typography>
          <div className={classes.testimonialAuthor}>
            <div className={classes.avatarCircle}>
              {testimonials[activeIndex].initials}
            </div>
            <div className={classes.authorInfo}>
              <div className={classes.authorName}>{testimonials[activeIndex].name}</div>
              <div className={classes.authorRole}>
                {testimonials[activeIndex].case} | {testimonials[activeIndex].location}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.navigationArrows}>
          <IconButton 
            className={classes.navArrow} 
            onClick={handlePrev}
            disabled={activeIndex === 0}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton 
            className={classes.navArrow} 
            onClick={handleNext}
          >
            <ArrowForwardIcon />
          </IconButton>
        </div>

        <div className={classes.indicatorsContainer}>
          {testimonials.map((_, index) => (
            <div 
              key={index}
              className={`${classes.indicator} ${index === activeIndex ? classes.activeIndicator : ''}`}
              onClick={() => goToTestimonial(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default withStyles(styles)(TestimonialsSection);